import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  Box,
  Chip,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import dayjs from 'dayjs';
import { getActionStyles, findPositionLabel } from './TireHistoricalPage';
import { useLanguage } from '../../../../utils/i18n';
import { fleetApi } from '../../../../api/axiosClient';

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5 }}>
      {value || '-'}
    </Typography>
  </Box>
);

export default function TransactionDetailModal({ open, onClose, data }) {
  const { t } = useLanguage();
  const [scrapImage, setScrapImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      if (!data || data.transaction_type !== 'SCRAP' || !data.tire_item) {
        setScrapImage(null);
        return;
      }
      setImageLoading(true);
      setImageError(false);
      try {
        const response = await fleetApi.get(`/tire-batch/images/${data.tire_item}`);
        const imageUrl = response?.data?.data?.[0] || response?.data?.[0];
        if (imageUrl && typeof imageUrl === 'string') {
          setScrapImage(imageUrl);
        } else {
          setImageError(true);
        }
      } catch (err) {
        console.error('Failed to fetch scrap image', err);
        setImageError(true);
      } finally {
        setImageLoading(false);
      }
    };

    if (open) fetchImage();
  }, [open, data]);

  if (!data) return null;

  const actionStyles = getActionStyles(data.transaction_type);

  const formatCurrency = (val) => {
    if (val == null) return '-';
    return `Rp ${Number(val).toLocaleString('id-ID')}`;
  };

  const specString = [data.tire_spec_name, data.tire_brand_name, data.tire_size_code_internal || data.tire_size_code]
    .filter(Boolean)
    .join(' - ');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1.25rem', borderBottom: '1px solid #e2e8f0', p: 3 }}>
        {t('tireHistorical.transactionDetail', 'Transaction Detail')}
      </DialogTitle>
      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        <Grid container spacing={3}>
          {/* General Information Card */}
          <Grid item size={{ xs: 12, md: data.transaction_type === 'SCRAP' || data.transaction_type === 'REPAIR' ? 6 : 12 }}>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 2.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  {t('tireHistorical.generalInformation', 'General Information')}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item size={{ xs: 6 }}>
                    <InfoItem label={t('tireRepairScrap.serialNumberExport', 'Serial Number')} value={data.serial_number} />
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireRepairScrap.date', 'Date')}
                      value={data.transaction_date ? dayjs(data.transaction_date).format('DD MMM YYYY') : '-'}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}
                      >
                        {t('tireCardHistory.actionType', 'Action Type')}
                      </Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={data.transaction_type || '-'}
                          size="small"
                          sx={{
                            bgcolor: actionStyles.bg,
                            color: actionStyles.color,
                            fontWeight: 700,
                            borderRadius: 1,
                            px: 1
                          }}
                        />
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <InfoItem label={t('tireHistorical.specification', 'Specification')} value={specString} />
                  </Grid>

                  <Grid item size={{ xs: 6 }}>
                    <InfoItem label={t('tireCardHistory.vehicle', 'Vehicle')} value={data.license_plate} />
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireCycle.position', 'Position')}
                      value={findPositionLabel(data.current_position, data.serial_number)}
                    />
                  </Grid>

                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireCardHistory.kmUnit', 'KM Unit')}
                      value={data.km_unit ? Number(data.km_unit).toLocaleString('id-ID') : '-'}
                    />
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireCardHistory.hmUnit', 'HM Unit')}
                      value={data.hm_unit ? Number(data.hm_unit).toLocaleString('id-ID') : '-'}
                    />
                  </Grid>

                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireCycle.airPressureTh', 'Air Pressure')}
                      value={data.air_pressure != null ? `${data.air_pressure} psi` : '-'}
                    />
                  </Grid>
                  <Grid item size={{ xs: 6 }}>
                    <InfoItem
                      label={t('tireCycle.treadDepthTh', 'Tread Depth')}
                      value={data.tread_depth != null ? `${data.tread_depth} mm` : '-'}
                    />
                  </Grid>

                  <Grid item size={{ xs: 12 }}>
                    <InfoItem label={t('tireCardHistory.mechanicName', 'Mechanic Name')} value={data.mechanic_name} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Conditional Info Card (REPAIR) */}
          {data.transaction_type === 'REPAIR' && (
            <Grid item size={{ xs: 12, md: 6 }}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%', bgcolor: '#fffbf1' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 2.5, color: 'warning.dark', display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {t('tireRepairScrap.repairInformation', 'Repair Information')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12 }}>
                      <InfoItem label={t('tireCardHistory.tireDamage', 'Tire Damage')} value={data.tire_damage_name} />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.costRepair', 'Cost Repair')} value={formatCurrency(data.cost_repair)} />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.repairType', 'Repair Type')} value={data.repair_type} />
                    </Grid>

                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.vendorName', 'Vendor Name')} value={data.vendor_name} />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.vendorType', 'Vendor Type')} value={data.vendor_type} />
                    </Grid>

                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.workOrder', 'Work Order')} value={data.work_order_number} />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.actionStatus', 'Action Status')} value={data.action_status} />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <InfoItem label={t('tireCardHistory.notes', 'Notes')} value={data.notes} />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Conditional Info Card (SCRAP) */}
          {data.transaction_type === 'SCRAP' && (
            <Grid item size={{ xs: 12, md: 6 }}>
              <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', height: '100%', bgcolor: '#fef2f2' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 2.5, color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    {t('tireRepairScrap.scrapInformation', 'Scrap Information')}
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12 }}>
                      <InfoItem label={t('tireCardHistory.tireDamage', 'Tire Damage')} value={data.tire_damage_name} />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem
                        label={t('tireCardHistory.finalRtd', 'Final RTD')}
                        value={data.final_rtd != null ? `${data.final_rtd} mm` : '-'}
                      />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <InfoItem label={t('tireCardHistory.reason', 'Reason')} value={data.reason} />
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <InfoItem label={t('tireCardHistory.notes', 'Notes')} value={data.notes} />
                    </Grid>

                    <Grid item size={{ xs: 12 }}>
                      <Divider sx={{ my: 1, borderColor: '#fecaca' }} />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500, display: 'block', mb: 1 }}
                      >
                        {t('tireHistorical.scrapImage', 'Scrap Image')}
                      </Typography>
                      <Box
                        sx={{
                          width: '100%',
                          height: 200,
                          bgcolor: '#fff',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px dashed #fca5a5',
                          overflow: 'hidden'
                        }}
                      >
                        {imageLoading ? (
                          <CircularProgress size={30} color="error" />
                        ) : imageError || !scrapImage ? (
                          <Typography variant="body2" color="error.light" sx={{ fontWeight: 500 }}>
                            {t('tireHistorical.noImageAvailable', 'No Image Available')}
                          </Typography>
                        ) : (
                          <img src={scrapImage} alt="Scrap" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{ bgcolor: '#475569', '&:hover': { bgcolor: '#334155' }, textTransform: 'none', px: 3, fontWeight: 600 }}
        >
          {t('tireRepairScrap.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
